import React, { useMemo } from 'react';

// Esri World Imagery XYZ → basin equirect frame (same letterbox as the swell
// video). Near-equator Mercator stretch is negligible for Rote. Tile host sends
// Access-Control-Allow-Origin: * so <img> loads are fine.

const TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const MAX_TILES = 48;
const Z_MIN = 8;
const Z_MAX = 16;

function lon2tile(lon, z) {
  return Math.floor(((lon + 180) / 360) * 2 ** z);
}

function lat2tile(lat, z) {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * 2 ** z);
}

function tile2lon(x, z) {
  return (x / 2 ** z) * 360 - 180;
}

function tile2lat(y, z) {
  const n = Math.PI - (2 * Math.PI * y) / 2 ** z;
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
}

function chooseZoom(lonMin, lonMax, latMin, latMax, stageW, mapZoom) {
  const lonSpan = Math.max(0.01, lonMax - lonMin);
  const midLat = (latMin + latMax) / 2;
  const targetPx = Math.max(256, (stageW || 640) * Math.max(1, mapZoom));
  let z = Math.round(Math.log2((targetPx * 360) / (lonSpan * 256 * Math.max(0.2, Math.cos((midLat * Math.PI) / 180)))));
  z = Math.max(Z_MIN, Math.min(Z_MAX, z));
  for (; z >= Z_MIN; z -= 1) {
    const count = (lon2tile(lonMax, z) - lon2tile(lonMin, z) + 1)
      * (lat2tile(latMin, z) - lat2tile(latMax, z) + 1);
    if (count <= MAX_TILES) return z;
  }
  return Z_MIN;
}

/**
 * High-res imagery underlay for a regional swell map stage.
 * @param {{
 *   doc: { latMin:number, latMax:number, lonMin:number, lonMax:number, width?:number, height?:number },
 *   stageW: number,
 *   stageH: number,
 *   mapZoom?: number,
 *   opacity?: number,
 * }} props
 */
export default function BasinImageryTiles({ doc, stageW, stageH, mapZoom = 1, opacity = 1 }) {
  const layout = useMemo(() => {
    const { latMin, latMax, lonMin, lonMax } = doc || {};
    if (![latMin, latMax, lonMin, lonMax].every(Number.isFinite)) return null;
    if (!stageW || !stageH) return null;
    const VW = Number.isFinite(doc.width) ? doc.width : stageW;
    const VH = Number.isFinite(doc.height) ? doc.height : stageH;
    const scale = Math.min(stageW / VW, stageH / VH);
    const dispW = VW * scale;
    const dispH = VH * scale;
    const offX = (stageW - dispW) / 2;
    const offY = (stageH - dispH) / 2;
    const lonSpan = lonMax - lonMin || 1;
    const latSpan = latMax - latMin || 1;

    const project = (lon, lat) => ({
      x: ((lon - lonMin) / lonSpan) * dispW,
      y: ((latMax - lat) / latSpan) * dispH,
    });

    const z = chooseZoom(lonMin, lonMax, latMin, latMax, dispW, mapZoom);
    const x0 = lon2tile(lonMin, z);
    const x1 = lon2tile(lonMax, z);
    const y0 = lat2tile(latMax, z);
    const y1 = lat2tile(latMin, z);
    const tiles = [];
    for (let x = x0; x <= x1; x += 1) {
      for (let y = y0; y <= y1; y += 1) {
        const west = tile2lon(x, z);
        const east = tile2lon(x + 1, z);
        const north = tile2lat(y, z);
        const south = tile2lat(y + 1, z);
        const tl = project(west, north);
        const br = project(east, south);
        tiles.push({
          key: `${z}/${y}/${x}`,
          url: TILE_URL.replace('{z}', String(z)).replace('{y}', String(y)).replace('{x}', String(x)),
          left: tl.x,
          top: tl.y,
          width: Math.max(1, br.x - tl.x),
          height: Math.max(1, br.y - tl.y),
        });
      }
    }
    return { offX, offY, dispW, dispH, tiles, z };
  }, [doc, stageW, stageH, mapZoom]);

  if (!layout?.tiles?.length) return null;

  return (
    <div
      className="cs-rote-map-tiles"
      style={{
        left: layout.offX,
        top: layout.offY,
        width: layout.dispW,
        height: layout.dispH,
        opacity,
      }}
      aria-hidden
      data-z={layout.z}
    >
      {layout.tiles.map((t) => (
        <img
          key={t.key}
          className="cs-rote-map-tile"
          src={t.url}
          alt=""
          draggable={false}
          loading="lazy"
          decoding="async"
          style={{ left: t.left, top: t.top, width: t.width, height: t.height }}
        />
      ))}
      <span className="cs-rote-map-tiles-credit">© Esri · Maxar · Earthstar</span>
    </div>
  );
}
